import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { PrivilegesService } from '../../../shared/services/privileges.service';
import { LayoutService } from '../../layout.service';
import { TreeControllerComponent } from '../../../pages/tree/tree-controller/tree-controller.component';
import { TreeNode } from '../../../pages/tree/tree-controller/tree-async/models/TreeNode';
import { getNodeUrl } from '../../../pages/tree/tree-controller/tree-async/tree-node/getNodeUrl';
import { delay, forkJoin, fromEvent, map, of } from 'rxjs';
import { FindOnTreeRequestDto } from '../../../../api-main';
import { TreeNodeActionType } from '../../../pages/tree/tree-controller/tree-async/models/TreeNodeActionType';
import { PresetTreeElementsService } from '../../../pages/tree/tree-controller/tree-async/preset-tree-elements.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * Manage navigation based on routing - jump to proper dashboard, device etc.
 * Create tree template items that exist in every organization
 */
@Component({
  selector: 'app-main-layout-tree',
  templateUrl: './main-layout-tree.component.html',
  styleUrls: ['./main-layout-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TreeControllerComponent],
})
export class MainLayoutTreeComponent implements OnInit {
  private router = inject(Router);
  private privilegesService = inject(PrivilegesService);
  private layoutService = inject(LayoutService);
  private presetElementsService = inject(PresetTreeElementsService);

  @Output() nodeSelected = new EventEmitter<void>();

  unsubscribe$ = takeUntilDestroyed();

  ngOnInit() {
    //FIX for back/forward browser events
    fromEvent(window, 'popstate')
      .pipe(delay(10), this.unsubscribe$) // Cannot use takeUntilDestroyed directly, it is allowed only at constructor
      .subscribe(() => {
        this.getItemFromRoute();
      });
    this.getItemFromRoute();
  }

  private getItemFromRoute() {
    const url = this.router.url;
    const isTreeNode = new RegExp('/tree/inspect/[0-9]+').test(url);
    const isLogger = new RegExp('/device/[0-9]+').test(url);
    const isDashboard = new RegExp('/dashboards/[0-9]+').test(url);
    const isMeterOnMCenter = new RegExp('/meter/[0-9]+/[0-9]+').test(url);
    // Meter on tree is presented with tree.item_tree.id
    const isMeterOnTree = new RegExp('/meter/[0-9]+').test(url);
    const isOrganizationPage = new RegExp('organization/[0-9]+').test(url);

    if (!isTreeNode && !isLogger && !isDashboard && !isMeterOnMCenter && !isMeterOnTree && !isOrganizationPage) {
      // console.log('getItemFromRoute - handlePresetElement');
      this.handlePresetElement();
      return;
    }

    if (isOrganizationPage) {
      this.layoutService.treeNodeService.nodeAction$.next({
        node: {
          id: this.presetElementsService.rootId,
          pointingUrl: `/organization/${this.privilegesService.organization()?.id}`,
        } as TreeNode,
        action: TreeNodeActionType.ON_CLICK,
      });
      return;
    }

    const id = this.extractLastIdFromUrl(url);
    if (!id) return;
    const request: FindOnTreeRequestDto = {};

    if (isDashboard) request.dashboard_id = id;
    if (isLogger) request.logger_id = id;
    if (isTreeNode) request.id = id;
    if (isMeterOnTree) request.id = id;
    if (isMeterOnMCenter) {
      const ids = this.extractOneBeforeLastFromUrl(url);
      if (!ids) {
        return;
      }
      // That's a virtual device, don't have any information if it is on the tree or not.
      // Jump into M-Center.
      request.logger_id = ids.logger_id;
    }
    // console.log('getItemFromRoute - jump to ', request);
    this.layoutService.jumpToTreeNode(request);
  }

  private handlePresetElement() {
    const url = this.router.url;
    // console.log('handlePresetElements for URL: ', url);
    const elements = this.presetElementsService.getPresetElements();
    const children = elements.map((o) => o.presetChildren).filter((o) => !!o);

    //gymnastic to retrieve presetChildren which are observables
    //children first to get proper url
    forkJoin([...children, ...elements.map((o) => of(o))])
      // .pipe(switchMap((o) => o))
      .pipe(map((o) => o.flat()))
      .subscribe((presetElements) => {
        const item = presetElements.find((o) => url.includes(o.pointingUrl));
        if (!item) return;
        // console.log('handlePresetElement nodeAction click on ', item);
        this.layoutService.treeNodeService.expandedNodes.add(item.parent_id);
        this.layoutService.treeNodeService.nodeAction$.next({
          node: item,
          action: TreeNodeActionType.ON_CLICK,
        });
      });
  }

  // Get last number from URL /something/:this_number
  private extractLastIdFromUrl(url: string = this.router.url): number {
    const match = url.match(/\/(\d+)$/);
    const id = match ? match[1] : null;
    return Number(id);
  }

  // Used for getting logger id from URL  /meter/:logger_id/:meter_id
  private extractOneBeforeLastFromUrl(url: string = this.router.url): {
    logger_id: number;
    meter_id: number;
  } {
    const match = url.match(/\/(\d+)\/(\d+)$/);
    if (!match) return null;
    if (match.length !== 3) return null;
    return {
      logger_id: Number(match[1]),
      meter_id: Number(match[2]),
    };
  }

  itemSelected(item: TreeNode) {
    // console.log('=>(main-layout-tree.component.ts) itemSelected', item);
    this.layoutService.selectedItem = item;
    // console.log('main layout item selected -> item', item);

    // redirect to the right place
    if (
      item &&
      item.isPreSetElement &&
      item.pointingUrl &&
      !(this.router.url === item.pointingUrl) // Do not jump into the same place
    ) {
      this.navigate(item.pointingUrl);
      this.nodeSelected.emit();
    }

    if (item && !item.isPreSetElement) this.navigate(getNodeUrl(item));

    if (item && (item.is_end_type || item.id === 0)) this.nodeSelected.emit();
  }

  private navigate(url: string) {
    this.router.navigate([url]).catch(console.error);
  }
}
